import type { ChainKey } from 'src/blockchain';
import type { ApiListingItem } from './listing';
import type { ApiDeliveryEscrowTerms } from './escrow';
import type { DeliveryVote, ApiDealStepEvent } from './dealStep';

/**
 * Frontend mirror of the delivery read/write API DTOs (deal v2 "Эскроу с доставкой",
 * spec §7). The buyer surface is a wallet flow gated on a linked account
 * (@Protected({ forWallet, forAccount })).
 *
 * Source of truth: api `src/infrastructure/dto/delivery/*` + `src/domain/types/delivery/*`.
 * Amounts here are plain USDT numbers (NOT base units); `gasTrxAmount` is TRX as a
 * string. The fund() transfer reads exact base units on-chain (see deliveryOnchain.ts).
 */

/** Backend status of a delivery deal (1:1 with api DeliveryDealStatus, spec §4.9). */
export enum DeliveryDealStatus {
  // Offer phase (platform-driven)
  Offered = 'offered',
  Approved = 'approved',
  AwaitingPayment = 'awaiting_payment',
  // On-chain-projected (poller)
  Escrowed = 'escrowed',
  Disputed = 'disputed',
  PickedUp = 'picked_up',
  // Post-delivery (platform-driven)
  Delivered = 'delivered',
  HandedOver = 'handed_over',
  // Cancel / finalization (terminal)
  OfferExpired = 'offer_expired',
  Declined = 'declined',
  Withdrawn = 'withdrawn',
  ApprovalExpired = 'approval_expired',
  Cancelled = 'cancelled',
  Refunded = 'refunded',
}

/** Gas-compensation payment status (spec §4.3). */
export enum DeliveryGasStatus {
  Pending = 'pending',
  Paid = 'paid',
}

/** An active delivery city (GET /buyer/delivery/points). Price is quoted by the dealer. */
export interface ApiDeliveryPoint {
  id: string;
  city: string;
}

export interface ApiDeliveryPointList {
  items: ApiDeliveryPoint[];
}

/**
 * A delivery deal in the buyer projection (mirror of DeliveryDealBuyerDto). The
 * `handoverCode` is buyer-only and present only from status `escrowed` (spec §4.11).
 */
export interface ApiDeliveryDeal {
  id: string;
  listingId: string;
  buyerAccountId: string;
  buyerWalletId: string;
  sellerWalletId: string;
  logistPartnerId: string | null;
  /**
   * The network this deal settles on (contracts v3). Written by api when the offer is created and
   * never changed — the token, the factory and every payout address were resolved from it.
   *
   * Optional on purpose: rows written before the multichain track carry no value (api reads them
   * as TRON), and the field is absent altogether from an api older than v3. `resolveDealChain`
   * treats "absent" as the legacy single chain rather than guessing between several.
   */
  chainKey?: ChainKey;
  /**
   * Generation of the escrow contract the deal is deployed against (api#178): 1 = the TRON clone,
   * whose dispute is settled by GAC alone; 3 = the v3 clone, whose dispute the parties sign and
   * whose buyer may claim a stale dispute after 14 days. Stamped by api — the contracts do not
   * report it — so this, not the chain, says which buttons exist.
   *
   * Optional because the repos deploy apart: against an api older than the field the screen falls
   * back to the family (TRON = 1, EVM = 3), which is exact until v3 reaches TRON (hub §7.1).
   */
  contractVersion?: number;
  deliveryPointId: string;
  citySnapshot: string;
  recipientFullName: string;
  recipientPhone: string;
  /** Car price (USDT). */
  priceAmount: number;
  /** Platform fee (USDT). */
  feeAmount: number;
  /** Dealer delivery quote (USDT); null until Approve (spec §4.10). */
  deliveryAmount: number | null;
  /** Gas compensation (TRX), as a string. */
  gasTrxAmount: string;
  gasStatus: DeliveryGasStatus;
  gasTxHash: string | null;
  status: DeliveryDealStatus;
  offerExpiresAt: string;
  paymentExpiresAt: string | null;
  contractAddress: string | null;
  deployTxHash: string | null;
  handoverAttempts: number;
  orderRef: string;
  approvedAt: string | null;
  escrowedAt: string | null;
  pickedUpAt: string | null;
  /**
   * The logist's promised delivery day as `YYYY-MM-DD` (v3 §2.2) — the v3 SLA, replacing "72
   * hours". A day, not an instant: render it as it is, never through a local `Date`.
   */
  estimatedDeliveryAt: string | null;
  /** When the logist reported the car delivered to the pickup point. */
  deliveredAt: string | null;
  /** Start of the on-chain dispute clock; the 14-day stale-claim timeout counts from here. */
  disputeStartedAt: string | null;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string | null;
  /** Listing snapshot (null when the listing no longer exists). */
  listing: ApiListingItem | null;
  /** Handover code — buyer-only, present from status `escrowed` (spec §4.11). */
  handoverCode: string | null;
}

/**
 * What a single-deal answer carries on top of the row: the timeline the tracker renders and the
 * dispute mirror the buyer needs to act (contracts v3, api part §4).
 *
 * Deliberately absent from the list projection — api keeps lists light, and a list that carried
 * the timeline would fetch a history nobody is looking at, once per row.
 */
/** The logist on a deal as the buyer sees them (partners-spec §3.5): the company, its phone, its email. */
export interface ApiDeliveryLogist {
  name: string;
  phone: string | null;
  email: string;
}

export interface ApiDeliveryDealDetail extends ApiDeliveryDeal {
  /** The deal's timeline, oldest first. On-chain steps carry block time and a transaction hash. */
  stepEvents: ApiDealStepEvent[];
  /**
   * `Your logist` (partners-spec §3.5; api#300): present from `escrowed` — the money is on the
   * contract — and null before. Absent on an api older than api#300.
   */
  logist?: ApiDeliveryLogist | null;
  /**
   * The terms sheet the buyer's own wallet creates the escrow contract from (api#380): present
   * while the deal awaits a contract the buyer is to create — `awaiting_payment`, no
   * `contractAddress`, on a chain where the registry says the buyer creates it — and null
   * otherwise (once the address is recorded, on the platform-created path, on TRON). Optional
   * because the repos deploy apart: an api older than the field sends nothing, and the screen then
   * waits for the platform's deploy as before.
   */
  escrowTerms?: ApiDeliveryEscrowTerms | null;
  buyerVote: DeliveryVote;
  dealerVote: DeliveryVote;
  platformVote: DeliveryVote;
  /** When the buyer may take the refund alone (dispute start + 14 days); null outside a dispute. */
  staleClaimAvailableAt: string | null;
}

export interface ApiDeliveryDealList {
  items: ApiDeliveryDeal[];
  total: number;
}

/** Payload for creating a delivery offer (POST /buyer/delivery/deals). */
export interface ApiCreateDeliveryOfferPayload {
  listingId: string;
  deliveryPointId: string;
  recipientFullName: string;
  recipientPhone: string;
  /**
   * The buyer's tax number, required of every offer since v3 (§2.2) and snapshotted into the deal.
   * 4..64 characters; the format is not validated — it differs by country and a wrong guess would
   * refuse valid numbers. Visible to the dealer alone: the buyer's own projection omits it.
   */
  recipientTaxId: string;
}
